import React, { useState, useCallback } from 'react';
import { cacheAnalysisResult, getCachedResult, CACHE_TYPES } from '../services/cache';
import SearchLimitBanner from './SearchLimitBanner';
import { hasReachedLimit, incrementSearchCount } from '../services/searchLimit';
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Fade,
  TextField,
  Avatar,
  Collapse,
  Tooltip,
} from '@mui/material';
import { searchHashtagVideos, getAIInsights } from '../services/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

function HashtagAnalysis() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);
  const [hashtag, setHashtag] = useState('');
  const [searchedHashtag, setSearchedHashtag] = useState('');
  const [insightsExpanded, setInsightsExpanded] = useState(true);

  const handleSearch = useCallback(async (e, forceRefresh = false) => {
    e?.preventDefault();
    
    if (!hashtag.trim()) {
      setError('Please enter a hashtag to search');
      return;
    }
    
    if (loading) {
      console.log('Already loading, skipping search');
      return;
    }

    if (hasReachedLimit()) {
      setError('You have reached your daily search limit');
      return;
    }

    // Clear previous results when starting a new search
    setResults(null);
    setInsights(null);

    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cached = getCachedResult(CACHE_TYPES.HASHTAG, hashtag);
        if (cached?.data) {
          console.log('Using cached data for hashtag:', hashtag);
          setResults(cached.data);
          setSearchedHashtag(hashtag);
          
          // Dispatch event to update dashboard
          window.dispatchEvent(new CustomEvent('cacheUpdated', {
            detail: { key: `${CACHE_TYPES.HASHTAG}${hashtag}`, type: CACHE_TYPES.HASHTAG }
          }));
          
          // Get AI insights for the cached videos
          try {
            const insightsData = await getAIInsights(cached.data.videos);
            if (insightsData?.insights) {
              setInsights(insightsData.insights);
            }
          } catch (insightError) {
            console.error('Error getting insights for cached data:', insightError);
          }
          return;
        }
    }
    
    console.log('Starting search, setting loading state');
    setLoading(true);
    setError(null);
    setSearchedHashtag(hashtag);
    
    try {
      console.log('Searching hashtag videos...');
      const data = await searchHashtagVideos(hashtag, forceRefresh);
      console.log('Received data from API:', data);
      
      if (!data?.videos?.length) {
        console.error('No videos in response:', data);
        throw new Error(`No videos found for hashtag #${hashtag}`);
      }
      
      console.log('Setting results:', data);
      setResults(data);
      incrementSearchCount();
      
      // Cache the successful results and trigger dashboard update
      cacheAnalysisResult(CACHE_TYPES.HASHTAG, hashtag, data);
      
      // Dispatch event with aggregated data
      const allHashtags = new Set();
      let totalViews = 0, totalLikes = 0, totalComments = 0;
      
      // Get all hashtag data from localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_TYPES.HASHTAG)) {
          try {
            const storedData = JSON.parse(localStorage.getItem(key));
            if (storedData?.data) {
              allHashtags.add(storedData.data.hashtag);
              // eslint-disable-next-line no-loop-func
              storedData.data.videos?.forEach(video => {
                totalLikes += video.stats?.diggCount || 0;
                totalViews += video.stats?.playCount || 0;
                totalComments += video.stats?.commentCount || 0;
              });
            }
          } catch (error) {
            console.error('Error parsing stored hashtag data:', error);
          }
        }
      }

      window.dispatchEvent(new CustomEvent('cacheUpdated', {
        detail: {
          type: CACHE_TYPES.HASHTAG,
          totalHashtags: allHashtags.size,
          totalViews,
          totalLikes,
          totalComments
        }
      }));
      
      // Get AI insights for the videos
      try {
        const insightsData = await getAIInsights(data.videos);
        if (insightsData?.insights) {
          setInsights(insightsData.insights);
        }
      } catch (insightError) {
        console.error('Error getting insights:', insightError);
        // Don't fail the whole component if insights fail
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load content';
      console.error('Error fetching data:', errorMessage);
      setError(errorMessage);
      setResults(null);
      setInsights(null);
    } finally {
      setLoading(false);
    }
  }, [loading, hashtag]);

  const formatInsights = useCallback((insightsText) => {
    try {
      if (!insightsText || typeof insightsText !== 'string') return null;

      // Split insights into paragraphs
      const paragraphs = insightsText.split('\n').filter(p => p.trim());
      
      // Group paragraphs into sections
      const formattedContent = [];
      let currentSection = null;
      
      for (const paragraph of paragraphs) {
        // Check if this paragraph starts with a number, indicating a new section
        if (/^\d+\./.test(paragraph.trim())) {
          // Remove the number and any common prefixes
          const title = paragraph.replace(/^\d+\.\s*/, '').split(':')[0].trim();
          currentSection = {
            title: title,
            content: paragraph.split(':').slice(1).join(':').trim()
          };
          formattedContent.push(currentSection);
        } else if (currentSection) {
          // Append content to current section
          currentSection.content += '\n' + paragraph.trim();
        } else {
          // If no section yet, create a generic one
          formattedContent.push({
            title: 'Key Insights',
            content: paragraph.trim()
          });
        }
      }
      
      return formattedContent.filter(section => section.content.trim());
    } catch (error) {
      console.error('Error formatting insights:', error);
      return null;
    }
  }, []);

  return (
    <Box 
      sx={{ 
        py: 4,
        minHeight: '100vh',
      }}
    >
      <SearchLimitBanner />
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 4,
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
        borderRadius: 2,
        p: 3
      }}>
        <Avatar sx={{ 
          width: 56, 
          height: 56,
          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
        }}>
          <TrendingUpIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Hashtag Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Discover trending hashtags and optimize your content's reach
          </Typography>
        </Box>
      </Box>
      {/* Search Form with Help Tooltip */}
      <Box 
        component="form" 
        onSubmit={handleSearch}
        sx={{ 
          mb: 4,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          position: 'relative'
        }}
      >
        <Tooltip 
          title="Enter a hashtag without the # symbol to analyze its performance and get AI-powered insights"
          placement="top-start"
          arrow
        >
          <IconButton 
            size="small"
            sx={{ 
              position: 'absolute',
              right: -40,
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.6,
              '&:hover': { opacity: 1 }
            }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter hashtag to search (e.g. dance)"
          value={hashtag}
          onChange={(e) => setHashtag(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
          disabled={loading}
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>#</Typography>,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 30,
              bgcolor: 'white',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
              '&:hover': {
                boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
              }
            }
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading || !hashtag.trim()}
          sx={{ 
            height: 56,
            width: 56,
            minWidth: 56,
            borderRadius: 30,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
            },
          }}
        >
          <SearchIcon />
        </Button>
        {results && (
          <Button
            variant="outlined"
            onClick={(e) => handleSearch(e, true)}
            disabled={loading}
            sx={{ 
              height: 56,
              width: 56,
              minWidth: 56,
              borderRadius: 2,
            }}
          >
            <RefreshIcon />
          </Button>
        )}
      </Box>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* AI Insights at the top */}
      {results?.videos && !insights && (
        <Alert severity="info" sx={{ mb: 4 }}>
          AI analysis is currently unavailable. Showing top videos only.
        </Alert>
      )}
      {insights && (
        <Card 
          sx={{ 
            mb: 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
            color: 'white',
            boxShadow: '0px 10px 30px rgba(139, 92, 246, 0.3)',
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                  AI Insights for #{results?.hashtag}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => setInsightsExpanded(!insightsExpanded)}
                sx={{ 
                  transform: insightsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            <Collapse in={insightsExpanded}>
              {formatInsights(insights)?.map((section, index) => (
                <Box key={index} sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 500, opacity: 0.9 }}>
                    {section.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1, color: 'white', opacity: 0.8 }}>
                    {section.content}
                  </Typography>
                </Box>
              ))}
            </Collapse>
          </Box>
        </Card>
      )}

      {/* Loading indicator */}
      <Fade in={loading} unmountOnExit>
        <Box 
          sx={{ 
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'rgba(255,255,255,0.9)',
            p: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            {insights ? 'Analyzing trends...' : `Searching #${searchedHashtag || hashtag}...`}
          </Typography>
        </Box>
      </Fade>

      {/* Error message */}
      {error && !loading && !results && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            textAlign: 'center',
            px: 2
          }}
        >
          <Card sx={{ p: 3, maxWidth: 400 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Unable to Load Content
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleSearch}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Card>
        </Box>
      )}

      {/* Content */}
      <Fade in={!loading && !!results?.videos}>
        <Box>
              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TrendingUpIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h5" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Top Videos for #{results?.hashtag}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {results?.videos?.map((video) => (
                  <Grid item xs={12} sm={6} lg={4} xl={3} key={video.id}>
                    <Card 
                      component="a"
                      href={video.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.08)',
                        transition: 'all 0.3s ease',
                        background: 'white',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0px 15px 35px rgba(0, 0, 0, 0.12)',
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative', paddingTop: '177.77%', bgcolor: '#f0f0f0' }}>
                        <img
                          src={video.thumbnail}
                          alt={video.description}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar 
                            src={video.author.avatar} 
                            alt={video.author.nickname}
                            sx={{ width: 24, height: 24, mr: 1 }}
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#666',
                              fontWeight: 500,
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {video.author.nickname}
                          </Typography>
                        </Box>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 500,
                            color: '#1c1c1e',
                            mb: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '2.5em'
                          }}
                        >
                          {video.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, color: '#666' }}>
                          <Typography variant="caption">{(video.stats?.playCount || 0).toLocaleString()} views</Typography>
                          <Typography variant="caption">{(video.stats?.diggCount || 0).toLocaleString()} likes</Typography>
                          <Typography variant="caption">{(video.stats?.commentCount || 0).toLocaleString()} comments</Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Box>
  );
}

export default HashtagAnalysis;
